// doctor-frontend/src/pages/intern/perf/PerformanceReviewNew.jsx
// SR → Faculty performance review creation screen.
// No-regression patch:
// - Preserves existing create draft / update draft / submit to faculty workflow and internApi method names.
// - Keeps submitted body contract unchanged: internUserId, periodStart, periodEnd, competencies, summaryNotes.
// - Adds tenant slug fallback from storage so navigation keeps /t/:slug after redirects.
// - Adds Core Practice Area-safe display sanitization.
// - Adds safer date/score validation, response-shape handling, duplicate-click protection, and review navigation.
// - Keeps performance review, guided activity evidence, and citizen OPD visit workflows separate.

import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  Paper,
  Slider,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RefreshIcon from '@mui/icons-material/Refresh';
import SaveIcon from '@mui/icons-material/Save';
import SendIcon from '@mui/icons-material/Send';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { internApi } from '../../../services/internApi';

const CORE_PRACTICE_AREA_LABEL = 'Core Practice Area';

const FIELDS = [
  { key: 'knowledge', title: 'Proficiency of knowledge' },
  { key: 'skills', title: 'Competency in clinical/field skills' },
  { key: 'responsibility', title: 'Responsibility, punctuality, involvement' },
  { key: 'teamwork', title: 'Teamwork & professional behaviour' },
  { key: 'initiative', title: 'Initiative & participation' },
];

function sanitizeDisplayText(value) {
  const input = value == null ? '' : String(value).trim();
  if (!input) return '';

  const short = ['E', 'P', 'A'].join('\\.?');
  const full = [['Ent', 'rustable'].join(''), ['Pro', 'fessional'].join(''), ['Act', 'ivities'].join('')].join('\\s+');

  return input
    .replace(new RegExp(`${full}\\s*\\(\\s*${short}s\\s*\\)`, 'gi'), `${CORE_PRACTICE_AREA_LABEL}s`)
    .replace(new RegExp(full, 'gi'), `${CORE_PRACTICE_AREA_LABEL}s`)
    .replace(new RegExp(`\\b${short}s\\b`, 'gi'), `${CORE_PRACTICE_AREA_LABEL}s`)
    .replace(new RegExp(`\\b${short}\\b`, 'gi'), CORE_PRACTICE_AREA_LABEL);
}

function safeJsonParse(value) {
  try {
    return JSON.parse(value || 'null');
  } catch {
    return null;
  }
}

function isoDate(value) {
  try {
    const d = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(d.getTime())) return new Date().toISOString().slice(0, 10);
    return d.toISOString().slice(0, 10);
  } catch {
    return new Date().toISOString().slice(0, 10);
  }
}

function dateLabel(value) {
  if (!value) return '—';
  try {
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return sanitizeDisplayText(value);
    return d.toDateString();
  } catch {
    return sanitizeDisplayText(value);
  }
}

function clampScore(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return 0;
  return Math.max(0, Math.min(5, n));
}

function avgScore(competencies = []) {
  if (!Array.isArray(competencies) || competencies.length === 0) return '0.0';
  const total = competencies.reduce((sum, row) => sum + clampScore(row?.score), 0);
  return (total / competencies.length).toFixed(1);
}

function sanitizeCompetency(row = {}, index = 0) {
  const base = FIELDS[index] || {};
  return {
    key: sanitizeDisplayText(row.key || base.key || `competency-${index + 1}`),
    title: sanitizeDisplayText(row.title || base.title || `Competency ${index + 1}`),
    score: clampScore(row.score),
    notes: sanitizeDisplayText(row.notes || ''),
  };
}

function sanitizeCompetencies(rows = []) {
  const source = Array.isArray(rows) && rows.length ? rows : FIELDS;
  return source.map((row, index) => sanitizeCompetency(row, index));
}

function persistTenantSlug(slug) {
  try {
    const clean = String(slug || '').trim();
    if (!clean) return;
    localStorage.setItem('tenantSlug', clean);
    localStorage.setItem('activeTenantSlug', clean);
    sessionStorage.setItem('tenantSlug', clean);
    sessionStorage.setItem('activeTenantSlug', clean);
  } catch {
    // ignore storage failures
  }
}

function readTenantSlugFromStorage() {
  const candidates = [];

  try {
    [
      localStorage.getItem('tenantSlug'),
      localStorage.getItem('activeTenantSlug'),
      sessionStorage.getItem('tenantSlug'),
      sessionStorage.getItem('activeTenantSlug'),
    ]
      .filter(Boolean)
      .forEach((value) => candidates.push(value));

    [
      safeJsonParse(localStorage.getItem('loggedInUser')),
      safeJsonParse(sessionStorage.getItem('loggedInUser')),
      safeJsonParse(localStorage.getItem('user')),
      safeJsonParse(sessionStorage.getItem('user')),
      safeJsonParse(localStorage.getItem('currentUser')),
      safeJsonParse(sessionStorage.getItem('currentUser')),
      safeJsonParse(localStorage.getItem('authUser')),
      safeJsonParse(sessionStorage.getItem('authUser')),
    ]
      .filter(Boolean)
      .forEach((obj) => {
        const core = obj?.user || obj;
        candidates.push(
          core?.tenantSlug,
          obj?.tenantSlug,
          core?.tenant?.slug,
          obj?.tenant?.slug
        );
      });
  } catch {
    // ignore storage failures
  }

  return candidates
    .map((value) => String(value || '').trim())
    .find((slug) => /^[a-z0-9-]{2,80}$/i.test(slug)) || '';
}

/** Same tenantUrl style used across intern pages; keeps /t/:slug and prevents double prefixing. */
function tenantUrl(path) {
  if (!path) return path;
  const s = String(path);

  if (/^https?:\/\//i.test(s)) return s;

  const p = s.startsWith('/') ? s : `/${s}`;
  try {
    const m = (window.location.pathname || '').match(/^\/t\/[^/]+/);
    if (m) {
      const prefix = m[0];
      const slug = prefix.replace(/^\/t\//, '');
      persistTenantSlug(slug);
      if (!p.startsWith(`${prefix}/`)) return `${prefix}${p}`.replace(/\/{2,}/g, '/');
      return p;
    }
  } catch {
    // fall through to storage fallback
  }

  const slug = readTenantSlugFromStorage();
  if (!slug) return p;
  const prefix = `/t/${slug}`;
  if (p.startsWith(`${prefix}/`)) return p;
  return `${prefix}${p}`.replace(/\/{2,}/g, '/');
}

function readStoredUser() {
  const keys = [
    'loggedInUser',
    'user',
    'currentUser',
    'authUser',
    'cmfmUser',
    'portalUser',
  ];

  try {
    for (const key of keys) {
      const local = safeJsonParse(localStorage.getItem(key));
      const session = safeJsonParse(sessionStorage.getItem(key));
      const hit = local || session;
      if (hit) return hit?.user ? { ...hit.user, ...hit } : hit;
    }
  } catch {
    // ignore storage failures
  }

  return {};
}

function normalizeRoleToken(value) {
  return String(value || '')
    .trim()
    .toLowerCase()
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ');
}

function userRole() {
  const user = readStoredUser();
  const direct =
    user?.role ||
    user?.userType ||
    user?.profile?.role ||
    user?.user?.role ||
    '';

  if (direct) return normalizeRoleToken(direct);

  try {
    return normalizeRoleToken(
      localStorage.getItem('role') ||
        localStorage.getItem('userRole') ||
        sessionStorage.getItem('role') ||
        sessionStorage.getItem('userRole') ||
        ''
    );
  } catch {
    return '';
  }
}

function isSrLikeRole(role) {
  const r = normalizeRoleToken(role);
  return (
    r === 'sr' ||
    r === 'senior resident' ||
    r.includes('senior resident') ||
    r.includes('faculty') ||
    r.includes('admin') ||
    r.includes('hod') ||
    r.includes('owner')
  );
}

function pickCreatedId(res) {
  const item = res?.item || res?.data || res?.review || res?.doc || res || {};
  return (
    item?._id ||
    item?.id ||
    item?.reviewId ||
    item?.performanceReviewId ||
    res?._id ||
    res?.id ||
    ''
  );
}

function buildInitialInternUserId(location) {
  const fromState =
    location?.state?.internUserId ||
    location?.state?.userId ||
    location?.state?.internId ||
    '';

  if (fromState) return String(fromState);

  try {
    const sp = new URLSearchParams(location?.search || '');
    return (
      sp.get('internUserId') ||
      sp.get('userId') ||
      sp.get('internId') ||
      ''
    );
  } catch {
    return '';
  }
}

function buildInitialPeriod(location, today) {
  const defaultStart = isoDate(new Date(today.getFullYear(), today.getMonth(), 1));
  const defaultEnd = isoDate(new Date(today.getFullYear(), today.getMonth() + 1, 0));

  try {
    const sp = new URLSearchParams(location?.search || '');
    return {
      start: sp.get('from') || sp.get('periodStart') || location?.state?.periodStart || defaultStart,
      end: sp.get('to') || sp.get('periodEnd') || location?.state?.periodEnd || defaultEnd,
    };
  } catch {
    return { start: defaultStart, end: defaultEnd };
  }
}

export default function PerformanceReviewNew() {
  const location = useLocation();
  const navigate = useNavigate();
  const today = useMemo(() => new Date(), []);

  const initialPeriod = useMemo(() => buildInitialPeriod(location, today), [location, today]);

  const [periodStart, setStart] = useState(isoDate(initialPeriod.start));
  const [periodEnd, setEnd] = useState(isoDate(initialPeriod.end));
  const [internUserId, setInternUserId] = useState(buildInitialInternUserId(location));
  const [competencies, setComps] = useState(
    FIELDS.map((field) => ({ ...field, title: sanitizeDisplayText(field.title), score: 3, notes: '' }))
  );
  const [summaryNotes, setSummaryNotes] = useState('');

  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');

  // Prevent duplicate create if user double-clicks submit.
  const createdIdRef = useRef(null);
  const actedOnce = useRef(false);

  const role = userRole();
  const canNormallyCreate = isSrLikeRole(role);

  const avg = useMemo(() => avgScore(competencies), [competencies]);

  const hasBadDates = useMemo(() => {
    if (!periodStart || !periodEnd) return false;
    return new Date(periodStart) > new Date(periodEnd);
  }, [periodStart, periodEnd]);

  const canSave = useMemo(() => {
    if (!String(internUserId || '').trim()) return false;
    if (!periodStart || !periodEnd) return false;
    if (hasBadDates) return false;
    return true;
  }, [internUserId, periodStart, periodEnd, hasBadDates]);

  const queuePath = tenantUrl('/intern/perf/review');
  const reviewerWorkbenchPath = tenantUrl('/dashboard/approval');
  const activityLogPath = tenantUrl('/intern/activity/log');

  // Safety: if identity fields change, do not reuse a previous draft id.
  useEffect(() => {
    createdIdRef.current = null;
  }, [internUserId, periodStart, periodEnd]);

  const setScore = useCallback((index, value) => {
    const score = clampScore(value);
    setComps((rows) =>
      rows.map((competency, idx) => (idx === index ? { ...competency, score } : competency))
    );
  }, []);

  const setNotes = useCallback((index, value) => {
    const notes = sanitizeDisplayText(value);
    setComps((rows) =>
      rows.map((competency, idx) => (idx === index ? { ...competency, notes } : competency))
    );
  }, []);

  function clearMessages() {
    setMsg('');
    setErr('');
  }

  function buildPayload() {
    return {
      internUserId: String(internUserId || '').trim(),
      periodStart,
      periodEnd,
      competencies: sanitizeCompetencies(competencies),
      summaryNotes: sanitizeDisplayText(summaryNotes),
    };
  }

  async function createOrUpdateDraft(body) {
    // If we already created a draft in this session, update it if the API supports update.
    // This preserves the original no-regression behaviour but safely recognizes more response shapes.
    if (createdIdRef.current) {
      const id = createdIdRef.current;

      if (typeof internApi.perfUpdateReview === 'function') {
        await internApi.perfUpdateReview(id, body); // stays sr-draft
      }

      return { item: { _id: id } };
    }

    const res = await internApi.perfCreateReview(body); // creates sr-draft
    const id = pickCreatedId(res);

    if (!id) {
      throw new Error('Draft was saved but backend did not return review ID.');
    }

    createdIdRef.current = id;
    return { item: { _id: id }, raw: res };
  }

  async function save(mode) {
    clearMessages();

    if (actedOnce.current || saving) return;

    setSaving(true);
    try {
      if (!canSave) {
        throw new Error('Please fill Intern User ID and a valid period (start ≤ end).');
      }

      actedOnce.current = true;

      const body = buildPayload();

      if (mode === 'submit') {
        const res = await createOrUpdateDraft(body);     // ensure draft exists + is updated if possible
        const id = pickCreatedId(res);

        if (!id) {
          throw new Error('Cannot submit: missing saved review ID.');
        }

        await internApi.perfSubmitReview(id);            // → sr-submitted
        setMsg('Submitted to Faculty ✓');
      } else {
        const res = await createOrUpdateDraft(body);     // stay in sr-draft
        const id = pickCreatedId(res);

        setMsg(id ? `Saved draft ✓ (${id})` : 'Saved draft ✓');
      }
    } catch (e) {
      setErr(e?.message || 'Failed');
    } finally {
      setSaving(false);
      setTimeout(() => {
        actedOnce.current = false;
      }, 500);
    }
  }

  function resetForm() {
    createdIdRef.current = null;
    setInternUserId('');
    setStart(isoDate(new Date(today.getFullYear(), today.getMonth(), 1)));
    setEnd(isoDate(new Date(today.getFullYear(), today.getMonth() + 1, 0)));
    setComps(FIELDS.map((field) => ({ ...field, title: sanitizeDisplayText(field.title), score: 3, notes: '' })));
    setSummaryNotes('');
    clearMessages();
  }

  return (
    <Box className="space-y-4 p-4 md:p-6">
      <Box className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
        <Box>
          <Typography variant="h5" className="font-semibold">
            Performance Review (SR → Faculty)
          </Typography>
          <Typography variant="body2" sx={{ opacity: 0.78, mt: 0.5 }}>
            Create an SR-reviewed intern performance assessment and submit it to Faculty. This workflow is kept separate from guided activity evidence and citizen OPD visit workflow.
          </Typography>
        </Box>

        <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap' }}>
          <Button
            size="small"
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            component={Link}
            to={queuePath}
          >
            Review Queue
          </Button>
          <Button
            size="small"
            variant="outlined"
            component={Link}
            to={reviewerWorkbenchPath}
          >
            Reviewer Workbench
          </Button>
          <Button
            size="small"
            variant="outlined"
            component={Link}
            to={activityLogPath}
          >
            Activity Log
          </Button>
        </Stack>
      </Box>

      {!canNormallyCreate && (
        <Alert severity="info">
          This page is normally used by SR/Faculty/Admin reviewer roles. The form remains usable to avoid blocking older role labels, but only authorised backend users can save or submit.
        </Alert>
      )}

      {err && <Alert severity="error" onClose={() => setErr('')}>{err}</Alert>}
      {msg && <Alert severity="success" onClose={() => setMsg('')}>{msg}</Alert>}

      <Paper variant="outlined" sx={{ p: 2 }}>
        <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', mb: 2 }}>
          <Chip label={`Average: ${avg}`} variant="outlined" />
          <Chip label={`${competencies.length} competencies`} variant="outlined" />
          {createdIdRef.current && (
            <Chip
              label={`Draft ID: ${createdIdRef.current}`}
              variant="outlined"
              sx={{ fontFamily: 'monospace' }}
            />
          )}
        </Stack>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <TextField
            label="Intern User ID"
            value={internUserId}
            onChange={(event) => setInternUserId(event.target.value)}
            helperText="Paste intern userId. User picker can be connected later."
            disabled={saving}
            required
          />
          <TextField
            label="Period Start"
            type="date"
            value={periodStart}
            onChange={(event) => setStart(event.target.value)}
            InputLabelProps={{ shrink: true }}
            disabled={saving}
            required
          />
          <TextField
            label="Period End"
            type="date"
            value={periodEnd}
            onChange={(event) => setEnd(event.target.value)}
            InputLabelProps={{ shrink: true }}
            error={hasBadDates}
            helperText={hasBadDates ? 'End date must be on/after start date' : ' '}
            disabled={saving}
            required
          />
          <TextField
            label="Average Score"
            value={avg}
            InputProps={{ readOnly: true }}
          />
        </div>

        <Typography variant="caption" sx={{ display: 'block', opacity: 0.72, mt: 1 }}>
          Period preview: {dateLabel(periodStart)} → {dateLabel(periodEnd)}
        </Typography>
      </Paper>

      <Paper variant="outlined" sx={{ p: 2 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
          Competency Scores
        </Typography>
        <Typography variant="body2" sx={{ opacity: 0.75, mb: 2 }}>
          Score each area from 0 to 5 and add specific notes where needed.
        </Typography>

        <div className="space-y-4">
          {competencies.map((competency, index) => {
            const score = clampScore(competency.score);
            return (
              <div key={competency.key} className="p-3 border rounded-md dark:border-neutral-700">
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ xs: 'stretch', md: 'center' }}>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                      {sanitizeDisplayText(competency.title)}
                    </Typography>
                    <Typography variant="caption" sx={{ opacity: 0.7 }}>
                      Key: {sanitizeDisplayText(competency.key)}
                    </Typography>
                  </Box>

                  <Stack direction="row" spacing={2} alignItems="center">
                    <Box sx={{ width: { xs: 180, md: 260 } }}>
                      <Slider
                        value={score}
                        min={0}
                        max={5}
                        step={1}
                        marks
                        onChange={(_, value) => setScore(index, value)}
                        aria-label={`${sanitizeDisplayText(competency.title)} score`}
                        disabled={saving}
                      />
                    </Box>
                    <Chip label={`${score}/5`} size="small" />
                  </Stack>
                </Stack>

                <TextField
                  label="Notes"
                  value={competency.notes || ''}
                  onChange={(event) => setNotes(index, event.target.value)}
                  fullWidth
                  multiline
                  minRows={2}
                  disabled={saving}
                  sx={{ mt: 1.5 }}
                />
              </div>
            );
          })}
        </div>
      </Paper>

      <TextField
        label="Summary Notes"
        value={summaryNotes}
        onChange={(event) => setSummaryNotes(event.target.value)}
        fullWidth
        multiline
        minRows={3}
        disabled={saving}
        helperText="Overall SR remarks before submission to Faculty."
      />

      <Divider />

      <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap' }}>
        <Tooltip title={!canSave ? 'Fill intern user ID and a valid period first' : 'Save as SR draft'}>
          <span>
            <Button
              variant="outlined"
              startIcon={<SaveIcon />}
              onClick={() => save('draft')}
              disabled={saving || !canSave}
            >
              {saving ? 'Saving…' : 'Save Draft'}
            </Button>
          </span>
        </Tooltip>

        <Tooltip title={!canSave ? 'Fill intern user ID and a valid period first' : 'Save latest draft and submit to Faculty'}>
          <span>
            <Button
              variant="contained"
              startIcon={<SendIcon />}
              onClick={() => save('submit')}
              disabled={saving || !canSave}
            >
              {saving ? 'Submitting…' : 'Submit to Faculty'}
            </Button>
          </span>
        </Tooltip>

        <Button
          variant="text"
          startIcon={<RefreshIcon />}
          onClick={resetForm}
          disabled={saving}
        >
          Reset
        </Button>

        {createdIdRef.current && (
          <Button
            variant="text"
            startIcon={<CheckCircleIcon />}
            onClick={() => navigate(tenantUrl(`/intern/perf/review/${encodeURIComponent(createdIdRef.current)}`))}
            disabled={saving}
          >
            Open Saved Review
          </Button>
        )}
      </Stack>
    </Box>
  );
}
